// EndOfWeekReport.tsx
// Migrated from End_of_WeekReport.aspx + End_of_WeekReport.css + header.css
// Every visual detail, colour, layout rule, and data shape mirrors the original VB/ASP.NET page.

import React, { useEffect, useState } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

type ActivityType = "G" | "T" | "L" | "" | null | undefined;

interface Activity {
  activityType: ActivityType;
  unitName: string;
  lessonName: string;
  timesDone: number;
  assessmentScore: string; // e.g. "85%" or "N/A"
  lastScore: string;       // e.g. "90%" or "NA"
  activityComment: string;
}

interface ReportData {
  pageTitle: string;
  reportHeading: string;
  logoUrl: string;
  studentName: string;
  weekEnding: string;
  gradeYear: number | string;
  timeSpent: string;
  activities: Activity[];
  mathsLabel: string;
  personalisedLabel: string;
}

// ─── Query-string helper (mirrors Request.QueryString access in VB) ───────────


const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

function useQueryParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    stuSch:       params.get("StuSch")       ?? "",
    stuFam:       params.get("StuFam")       ?? "",
    studentId:    params.get("StudentId")    ?? "",
    firstName:    params.get("FirstName")    ?? "",
    country:      params.get("Country")      ?? "",
    subject:      params.get("Subject")      ?? "",
    weekendDate:  params.get("WeekendDate")  ?? "",
    planCreatedOn:params.get("PlanCreatedOn")?? "",
  };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Mirrors the fixed dark-grey header bar with the distributor logo */
function Header({ logoUrl }: { logoUrl: string }) {
  return (
    <div style={styles.headerContainer}>
      {/* .scrolled_logo */}
      <div
        style={{
          ...styles.scrolledLogo,
          backgroundImage: `url('${logoUrl}')`,
        }}
        aria-label="Distributor logo"
      />
    </div>
  );
}

/** Mirrors .student_info_container — the two-column label/value block */
function StudentInfo({
  studentName,
  weekEnding,
  gradeYear,
  timeSpent,
}: Pick<ReportData, "studentName" | "weekEnding" | "gradeYear" | "timeSpent">) {
  const rows: [string, string | number][] = [
    ["Student Name", studentName],
    ["Week Ending",  weekEnding],
    ["Grade/Year",   gradeYear],
    ["Time Spent",   timeSpent],
  ];

  return (
    <div style={styles.studentInfoContainer}>
      {/* labels column */}
      <div style={styles.studentInfoLabels}>
        {rows.map(([label]) => (
          <p key={label} style={styles.sectionText}>
            {label}:
          </p>
        ))}
      </div>
      {/* values column */}
      <div style={styles.studentInfoContent}>
        {rows.map(([label, value]) => (
          <p key={label} style={styles.sectionText}>
            {value}
          </p>
        ))}
      </div>
    </div>
  );
}

/** Left-cell colour & icon logic — mirrors td_lsn / td_test / td_game */
function AssessmentCell({ activity }: { activity: Activity }) {
  const type = activity.activityType;

  if (type === "G") {
    return (
      <div style={{ ...styles.td, ...styles.tdLeft, ...styles.tdGame }}>
        {/* game_icon — background image from original CSS */}
        <div style={styles.gameIcon} />
        <span>GAME</span>
      </div>
    );
  }

  if (type === "T") {
    return (
      <div style={{ ...styles.td, ...styles.tdLeft, ...styles.tdTest }}>
        <span id="Score" style={styles.assessmentScoreText}>
          {activity.assessmentScore}
        </span>
        <br />
        <span>TEST</span>
      </div>
    );
  }

  // Lesson (L or default)
  return (
    <div style={{ ...styles.td, ...styles.tdLeft, ...styles.tdLsn }}>
      <span id="Score" style={styles.assessmentScoreText}>
        {activity.assessmentScore}
      </span>
    </div>
  );
}

/** Right-cell content — mirrors the .td_right block inside rptEWRLesson */
function ActivityCell({ activity }: { activity: Activity }) {
  const type = activity.activityType;

  // Game row: only Unit + Game name, no times/score/comment
  if (type === "G") {
    return (
      <div style={{ ...styles.td, ...styles.tdRight }}>
        <div style={styles.activityDescription}>
          <p style={styles.activityLabel}>
            Unit:{" "}
            <span style={styles.activityValue}>{activity.unitName}</span>
          </p>
          <p style={styles.activityLabel}>
            Game:{" "}
            <span style={styles.activityValue}>{activity.lessonName}</span>
          </p>
        </div>
      </div>
    );
  }

  const lessonLabel = type === "T" ? "Test" : "Lesson";

  return (
    <div style={{ ...styles.td, ...styles.tdRight }}>
      <div style={styles.activityDescription}>
        <p style={styles.activityLabel}>
          Unit:{" "}
          <span style={styles.activityValue}>{activity.unitName}</span>
        </p>
        <p style={styles.activityLabel}>
          {lessonLabel}:{" "}
          <span style={styles.activityValue}>{activity.lessonName}</span>
        </p>
        {/* Times Completed — T4508 fix: "Times Completed" (with space) */}
        <p style={{ ...styles.activityLabel, ...styles.activityTcompleted }}>
          Times Completed:{" "}
          <span style={styles.activityValue}>{activity.timesDone}</span>
        </p>
        {/* Last Score */}
        <p style={{ ...styles.activityLabel, ...styles.activityLscore }}>
          Last Score:{" "}
          <span style={styles.activityValue}>{activity.lastScore}</span>
        </p>
        {/* Comment */}
        {activity.activityComment && (
          <p
            style={styles.activityComment}
            dangerouslySetInnerHTML={{
              // VB inserted <br /> tags via CollectArray — render them as HTML
              __html: activity.activityComment,
            }}
          />
        )}
      </div>
    </div>
  );
}

/** Weekly Summary table — mirrors .weekly_summary_table + rptEWRLesson */
function WeeklySummaryTable({ activities }: { activities: Activity[] }) {
  return (
    <div style={styles.weeklySummaryTable}>
      {/* Heading row */}
      <div style={styles.tr}>
        <div style={{ ...styles.td, ...styles.tdHeading }}>
          Assessment Score
        </div>
        <div style={{ ...styles.td, ...styles.tdHeading }}>Activity</div>
      </div>

      {/* Data rows */}
      {activities.map((act, idx) => (
        <div key={idx} style={styles.tr}>
          <AssessmentCell activity={act} />
          <ActivityCell activity={act} />
        </div>
      ))}

      {activities.length === 0 && (
        <div style={styles.tr}>
          <div
            style={{
              ...styles.td,
              gridColumn: "1 / -1",
              textAlign: "center",
              color: "#808080",
              padding: "30px",
            }}
          >
            No activities found for this week.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function EndOfWeekReport() {
  const params = useQueryParams();
  const [data, setData]       = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    // Guard: mirror VB's early exit when required params are absent
    if (
      !params.stuSch ||
      !params.stuFam ||
      !params.studentId ||
      !params.country ||
      !params.subject
    ) {
      setError("Missing required query parameters.");
      setLoading(false);
      return;
    }

    const qs = new URLSearchParams({
      StuSch:    params.stuSch,
      StuFam:    params.stuFam,
      StudentId: params.studentId,
      FirstName: params.firstName,
      Country:   params.country,
      Subject:   params.subject,
      ...(params.weekendDate   ? { WeekendDate:   params.weekendDate }   : {}),
      ...(params.planCreatedOn ? { PlanCreatedOn: params.planCreatedOn } : {}),
    });

    fetch(`${API_BASE_URL}api/end-of-week-report/?${qs.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Server returned ${r.status}`);
        return r.json() as Promise<ReportData>;
      })
      .then((d) => {
        setData(d);
        // Mirror: document.title = PageTitle
        document.title = d.pageTitle;
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []); // run once on mount, identical to Page_Load IsPostBack=False

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading report…</p>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div style={styles.loadingContainer}>
        <p style={{ color: "red" }}>{error ?? "Unknown error"}</p>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── HEADER — mirrors <div class="header_container"> ── */}
      <Header logoUrl={data.logoUrl} />

      {/* ── SECTION 1: heading + student info ── */}
      <div style={styles.sectionDiv}>
        {/* .report_heading */}
        <div style={styles.reportHeading}>
          <span id="ReportHeading">{data.reportHeading}</span>
        </div>

        <StudentInfo
          studentName={data.studentName}
          weekEnding={data.weekEnding}
          gradeYear={data.gradeYear}
          timeSpent={data.timeSpent}
        />

        {/* Print button is commented out in original ASPX — keep hidden */}
        {/* <PrintButton /> */}
      </div>

      {/* ── SECTION 2: weekly summary table ── */}
      <div style={styles.sectionDiv}>
        <div style={styles.reportSubHeading}>Weekly Summary</div>
        <WeeklySummaryTable activities={data.activities} />
      </div>
    </>
  );
}

// ─── Styles (CSS-in-JS — 1-to-1 mapping to End_of_WeekReport.css + header.css) ──

const ORANGE_BORDER  = "#ffcca3";
const ORANGE_HEADER  = "#fd995d";
const ORANGE_LSN     = "#ffb179";
const PURPLE_TEST    = "#cc86bc";
const GREEN_GAME     = "#71c498";
const BLUE_SCORE     = "#0f5f9a";
const GREY_COMMENT   = "#808080";
const HEADER_BG      = "#333333";

const styles: Record<string, React.CSSProperties> = {
  // ── header.css ──────────────────────────────────────────────────────────
  headerContainer: {
    width:           "100vw",
    height:          40,
    backgroundColor: HEADER_BG,
    display:         "block",
    padding:         0,
    textAlign:       "center",
    color:           "#ffffff",
    marginBottom:    40,
    // matches fixed positioning from header.css #header
    position:        "fixed",
    top:             0,
    left:            0,
    zIndex:          99999,
  },
  scrolledLogo: {
    height:             40,
    width:              135,
    display:            "block",
    float:              "left",
    padding:            0,
    backgroundSize:     "contain",
    backgroundRepeat:   "no-repeat",
    cursor:             "default",   // T1604 cursor:default
  },

  // ── End_of_WeekReport.css ────────────────────────────────────────────────
  sectionDiv: {
    margin:    "0 auto",
    height:    "auto",
    width:     "80%",
    display:   "block",
    clear:     "both",
    padding:   0,
    overflow:  "hidden",
    // extra top padding on first section to clear fixed header
    paddingTop: 56,
  },
  reportHeading: {
    fontSize:   24,
    fontWeight: "bold",
  },
  reportSubHeading: {
    fontSize:   21,
    fontWeight: "bold",
  },

  // Student info
  studentInfoContainer: {
    fontSize: 18,
    overflow: "hidden",
  },
  studentInfoLabels: {
    display:      "block",
    float:        "left",
    marginBottom: 5,
    marginTop:    5,
    fontWeight:   "bold",
  },
  studentInfoContent: {
    display:      "block",
    float:        "left",
    marginBottom: 5,
    marginTop:    5,
    marginLeft:   15,
  },
  sectionText: {
    margin: "4px 0",
    padding: 0,
  },

  // Weekly summary table — mirrors .weekly_summary_table
  weeklySummaryTable: {
    width:           "100%",
    display:         "table",
    marginTop:       15,
    boxSizing:       "border-box",
    borderCollapse:  "collapse",
  },
  tr: {
    display:       "table-row",
    verticalAlign: "middle",
  },

  // Base table cell — .td
  td: {
    display:       "table-cell",
    padding:       25,
    border:        `5px solid ${ORANGE_BORDER}`,
    verticalAlign: "middle",
    boxSizing:     "border-box",
  },

  // .td_heading
  tdHeading: {
    fontWeight:      "bold",
    color:           "#ffffff",
    backgroundColor: ORANGE_HEADER,
  },

  // .td_left / .td_right
  tdLeft: {
    width:  "10%",
    height: 120,
    color:  "#ffffff",
  },
  tdRight: {
    width:  "90%",
    height: 120,
  },

  // Activity type backgrounds
  tdLsn: {
    backgroundColor: ORANGE_LSN,
    textAlign:       "center",
    fontSize:        24,
    fontWeight:      "bold",
  },
  tdTest: {
    backgroundColor: PURPLE_TEST,
    textAlign:       "center",
    fontSize:        24,
    fontWeight:      "bold",
  },
  tdGame: {
    backgroundColor: GREEN_GAME,
    textAlign:       "center",
    fontSize:        24,
    fontWeight:      "bold",
  },

  // Score text in left cell
  assessmentScoreText: {
    display: "block",
  },

  // .game_icon — background image set via CSS class in original
  gameIcon: {
    backgroundImage:    "url(/images/activity_icon_game.png)",
    height:             53,
    backgroundSize:     "contain",
    backgroundPosition: "center",
    backgroundRepeat:   "no-repeat",
    marginBottom:       4,
  },

  // Activity description right panel
  activityDescription: {
    padding: 0,
    margin:  0,
  },
  activityLabel: {
    fontWeight: "bold",
    padding:    2,
    margin:     2,
  },
  activityValue: {
    fontWeight: "normal",
  },
  // .activity_tcompleted
  activityTcompleted: {
    display: "inline",
    color:   BLUE_SCORE,
  },
  // .activity_lscore
  activityLscore: {
    display: "inline",
    color:   BLUE_SCORE,
  },
  // .activity_comment
  activityComment: {
    color:      GREY_COMMENT,
    fontWeight: "normal",
    padding:    2,
    margin:     2,
  },

  // Loading / error
  loadingContainer: {
    display:    "flex",
    alignItems: "center",
    justifyContent: "center",
    height:     "100vh",
  },
  loadingText: {
    fontSize: 18,
    color:    "#666",
  },
};

// ─── Global body styles (mirrors body {} in CSS) ─────────────────────────────
// Inject once — avoids a separate CSS file dependency
const globalStyle = document.createElement("style");
globalStyle.textContent = `
  // *, *::before, *::after { box-sizing: border-box; }
  body {
    font-family: Arial, 'Arimo', sans-serif;
    padding: 0;
    margin: 0;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  /* Responsive — mirrors @media screen and (max-width:740px) */
  @media screen and (max-width: 740px) {
    .ewr-section-div {
      width: 90% !important;
    }
  }
`;
if (!document.head.querySelector("[data-ewr-style]")) {
  globalStyle.setAttribute("data-ewr-style", "true");
  document.head.appendChild(globalStyle);
}